import type { BackofficeDashboardResponse, DashboardTileId } from '../types';
import { DashboardMetricTile } from './DashboardMetricTile';

export type DashboardTileDefinition = {
  id: DashboardTileId;
  title: string;
  description: string;
  linkTo?: string;
  defaultSize: { w: number; h: number };
  render: (
    data: BackofficeDashboardResponse,
    editing: boolean,
    tileId: DashboardTileId
  ) => ReactNode;
};

function number(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}
function currency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100);
}
export const DASHBOARD_TILE_DEFINITIONS: DashboardTileDefinition[] = [
  {
    id: 'organizations',
    title: 'Total organizations',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/organizations',
    description: 'Organizations created during the selected UTC date range.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Total organizations"
        value={number(d.organizations.createdInPeriod)}
        detail="created organizations"
        description="Organizations created during the selected UTC date range."
        linkTo="/organizations"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'users',
    title: 'Total users',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/users',
    description: 'Registered Nava user accounts created during the selected UTC date range.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Total users"
        value={number(d.users.createdInPeriod)}
        detail="created users"
        description="Registered Nava user accounts created during the selected UTC date range."
        linkTo="/users"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'studies',
    title: 'Total studies',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/projects',
    description:
      'Non-demo studies created during the selected UTC date range that have not been deleted.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Total studies"
        value={number(d.studies.createdInPeriod)}
        detail="created studies"
        description="Non-demo studies created during the selected UTC date range that have not been deleted."
        linkTo="/projects"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'active-studies',
    title: 'Active studies',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/projects?status=in_progress',
    description:
      'Non-demo, non-deleted studies whose current status is In progress. This is a current count and does not change with the selected date range.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Active studies"
        value={number(d.activeStudies.total)}
        detail="studies in progress"
        description="Non-demo, non-deleted studies whose current status is In progress. This is a current count and does not change with the selected UTC date range."
        linkTo="/projects?status=in_progress"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'interviews',
    title: 'Interviews collected',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/interviews',
    description:
      'Non-demo, non-preview interviews that started in the selected UTC date range and finished successfully. Failed and abandoned interviews are not included.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Interviews collected"
        value={number(d.interviews.finished)}
        detail={`${number(d.interviews.started)} started in selected period`}
        description="Non-demo, non-preview interviews that started in the selected UTC date range and finished successfully. Failed and abandoned interviews are not included."
        linkTo="/interviews"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'completion-rate',
    title: 'Interview completion rate',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/interviews',
    description:
      'The share of non-demo, non-preview interviews started in the selected UTC date range that finished successfully. It is shown as finished interviews divided by all interviews started; no rate is shown when none started.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Interview completion rate"
        value={
          d.interviews.started
            ? `${Math.round((d.interviews.finished / d.interviews.started) * 100)}%`
            : '—'
        }
        detail={`${number(d.interviews.finished)} of ${number(d.interviews.started)} finished`}
        description="The share of non-demo, non-preview interviews started in the selected UTC date range that finished successfully. It is shown as finished interviews divided by all interviews started; no rate is shown when none started."
        linkTo="/interviews"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'interview-quality',
    title: 'Interview quality',
    defaultSize: { w: 6, h: 2 },
    linkTo: '/interviews',
    description:
      'The quality-status breakdown for non-demo, non-preview interviews started in the selected UTC date range. Counts distinguish scored outcomes, outstanding QA, failed QA, unassessed interviews, and inapplicable assessment.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Interview quality"
        value={`${number(d.interviews.quality.completed)} completed`}
        description="The quality-status breakdown for non-demo, non-preview interviews started in the selected UTC date range. Counts distinguish scored outcomes, outstanding QA, failed QA, unassessed interviews, and inapplicable assessment."
        linkTo="/interviews"
        editing={e}
        tileId={tileId}
      >
        <dl className="mt-4 grid grid-cols-3 gap-2 text-xs text-neutral-600">
          <div>
            <dt>Incomplete</dt>
            <dd className="font-semibold">{number(d.interviews.quality.incomplete)}</dd>
          </div>
          <div>
            <dt>Invalid</dt>
            <dd className="font-semibold">{number(d.interviews.quality.invalid)}</dd>
          </div>
          <div>
            <dt>Pending</dt>
            <dd className="font-semibold">{number(d.interviews.quality.pending)}</dd>
          </div>
          <div>
            <dt>Not assessed</dt>
            <dd className="font-semibold">{number(d.interviews.quality.not_assessed ?? 0)}</dd>
          </div>
          <div>
            <dt>QA failed</dt>
            <dd className="font-semibold">{number(d.interviews.quality.qa_failed ?? 0)}</dd>
          </div>
          <div>
            <dt>Not applicable</dt>
            <dd className="font-semibold">{number(d.interviews.quality.not_applicable ?? 0)}</dd>
          </div>
        </dl>
      </DashboardMetricTile>
    ),
  },
  {
    id: 'active-organizations',
    title: 'Active organizations',
    defaultSize: { w: 2, h: 1 },
    linkTo: '/organizations',
    description:
      'Distinct organizations with at least one non-demo, non-preview interview started during the selected UTC date range. An organization is counted once even if it has many interviews.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Active organizations"
        value={number(d.activeOrganizations.total)}
        detail="Collected at least one interview"
        description="Distinct organizations with at least one non-demo, non-preview interview started during the selected UTC date range. An organization is counted once even if it has many interviews."
        linkTo="/organizations"
        editing={e}
        tileId={tileId}
      />
    ),
  },
  {
    id: 'payg-sales',
    title: 'Total PAYG sales',
    defaultSize: { w: 2, h: 1 },
    description:
      'USD net sales for one-time purchases completed during the selected UTC date range, minus USD refunds issued during that range. Auto-refill purchases are included; other currencies are excluded.',
    render: (d, e, tileId) => (
      <DashboardMetricTile
        title="Total PAYG sales"
        value={currency(d.paygSalesUsd.netAmountMinor)}
        detail="USD net sales"
        description="USD net sales for one-time purchases completed during the selected UTC date range, minus USD refunds issued during that range. Auto-refill purchases are included; other currencies are excluded."
        editing={e}
        tileId={tileId}
      />
    ),
  },
];
import type { ReactNode } from 'react';
